import React, { useState, useCallback } from 'react';
import { useRef, useEffect } from 'react';
import ProductCard from './ProductCard';
import { Link } from 'react-router-dom';

const ProductShowcase = ({ title, products, subtitle, loading, badge, showSeeMore = true, showExclusiveBadge = true, seeMoreLink }) => {
  const [mobileIndex, setMobileIndex] = useState(0);
  const mobileTrackRef = useRef(null);
  const touchStartX = useRef(0);

  const scrollMobileTo = useCallback((index) => {
    const clamped = Math.max(0, Math.min(index, products.length - 2));
    setMobileIndex(clamped);
    if (mobileTrackRef.current) {
      const cardWidth = mobileTrackRef.current.offsetWidth / 2;
      mobileTrackRef.current.scrollTo({
        left: clamped * cardWidth,
        behavior: 'smooth',
      });
    }
  }, [products.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      scrollMobileTo(mobileIndex + (diff > 0 ? 1 : -1));
    }
  };

  const trackRef = useRef(null);
  const copiesCount = Math.max(8, products ? products.length * 2 : 8);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !products || products.length === 0) return;

    let animationId;
    let position = 0;
    const speed = 0.5;

    const getSingleCopyWidth = () => {
      const totalChildren = track.children.length;
      const singleCount = Math.ceil(totalChildren / copiesCount);
      if (totalChildren === 0) return 0;
      const itemWidth = track.scrollWidth / totalChildren;
      return itemWidth * singleCount;
    };

    const step = () => {
      position += speed;
      const singleWidth = getSingleCopyWidth();
      if (singleWidth > 0 && position >= singleWidth) {
        position = position - singleWidth;
      }
      track.style.transform = `translateX(-${position}px)`;
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);

    const pause = () => cancelAnimationFrame(animationId);
    const resume = () => { animationId = requestAnimationFrame(step); };

    track.parentElement.addEventListener('mouseenter', pause);
    track.parentElement.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animationId);
      track.parentElement.removeEventListener('mouseenter', pause);
      track.parentElement.removeEventListener('mouseleave', resume);
    };
  }, [products, copiesCount]);

  if (!loading && (!products || products.length === 0)) {
    return null;
  }

  return (
    <section className="relative py-12">
      <div
        className="absolute inset-0 left-[50%] right-[50%] -mx-[50vw] -z-10"
        style={{
          background: 'linear-gradient(to right, var(--color-gold-light, #e6f4f5), var(--color-gold, #007A87) 8%, var(--color-gold, #007A87) 92%, var(--color-gold-light, #e6f4f5))'
        }}
      ></div>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">{title}</h2>
            {subtitle && (
              <p className="text-gray-600 mt-2 text-sm">{subtitle}</p>
            )}
            <div className="w-16 h-0.5 bg-gold mx-auto mt-2"></div>
          </div>
          {!loading && showSeeMore && (
            <Link
              to={seeMoreLink || '/shop-all'}
              className="group inline-flex items-center gap-2 px-6 py-2 border border-gold text-gold font-medium rounded-lg hover:bg-gold hover:text-white transition-all duration-300 ease-in-out text-sm"
            >
              See More
              <span className="transform translate-x-0 group-hover:translate-x-1 transition-transform duration-300">
                →
              </span>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-[3/4] rounded-xl mb-4"></div>
                <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
                <div className="bg-gray-200 h-4 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* MOBILE CAROUSEL — visible only on mobile */}
            <div className="block sm:hidden">
              <div
                ref={mobileTrackRef}
                className="flex gap-3 overflow-x-auto scroll-smooth scrollbar-hide"
                style={{ scrollSnapType: 'x mandatory' }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                {products.map((product, index) => (
                  <div
                    key={product.id || index}
                    className="flex-shrink-0 w-[47%]"
                    style={{ scrollSnapAlign: 'start' }}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>

              {/* Dots */}
              {products.length > 2 && (
                <div className="flex justify-center gap-1.5 mt-4">
                  {Array.from({ length: Math.ceil(products.length / 2) })
                    .map((_, i) => (
                      <button
                        key={i}
                        onClick={() => scrollMobileTo(i * 2)}
                        className={`rounded-full transition-all duration-300
                          ${Math.floor(mobileIndex / 2) === i || (i === Math.ceil(products.length / 2) - 1 && mobileIndex >= products.length - 2)
                            ? 'bg-gold w-4 h-2'
                            : 'bg-gray-300 w-2 h-2'
                          }`}
                      />
                    ))}
                </div>
              )}
            </div>

            {/* DESKTOP MARQUEE — hidden on mobile */}
            <div className="hidden sm:block">
              <div
                className="overflow-hidden w-full"
                style={{
                  position: 'relative',
                  maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                }}
              >
                <div
                  ref={trackRef}
                  className="flex gap-4 md:gap-6 items-stretch"
                  style={{ width: 'max-content', willChange: 'transform' }}
                >
                  {Array.from({ length: copiesCount }).flatMap((_, copyIndex) =>
                    (products || []).map((product, index) => (
                      <div
                        key={`${copyIndex}-${product.id || index}`}
                        className="flex-shrink-0"
                        style={{ width: '180px' }}
                      >
                        <ProductCard
                          product={product}
                          badge={badge}
                          isNew={product.is_new}
                          isExclusive={showExclusiveBadge ? undefined : false}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ProductShowcase;
